import { Button, Collapse, Form, Input, Radio, Row, Select, Typography } from "antd";
import { IConfigureMetric, IMetricsMapping, ISampleDataPointDataType, MappingMode } from "./consts";
import { CiTrash } from "react-icons/ci";
import { FormulaInput } from "./FormulaInput";

import "./gps-metric-mapping.css"
import { useEffect } from "react";

const { Text } = Typography;
const { Panel } = Collapse;

export const Metric = ({
    status,
    data,
    metricsData,
    idx,
    updateFieldData,
    deleteConfigureMetric,
    collapsible = false,
    metricMappingSampleDatapoints,
    metricsBuiltinFormula,
    metricsMappingConversion,
    open = false,
    sampleData,
}: IMetric) => {
    const metricLabel = metricsData.find((e) => e.value === data.metric)?.label

    const [form] = Form.useForm();

    useEffect(() => {
        if (data) {
            form.setFieldsValue({
                metric: metricLabel || '',
                extractionApproach: data.extractionApproach,
                extractionText: data.extractionText,
                conversion: data.conversion,
                units: data.units,
            });
        }
    }, [data, idx]);


    return (
        <Collapse
            defaultActiveKey={open ? [idx] : undefined}
            style={{ backgroundColor: 'white', marginBottom: '10px' }}>
            <Panel
                key={idx}
                header={<Typography.Text strong style={{ color: 'black', paddingTop: '2px' }}>{`Configure Metric: ${metricLabel || ''}`}</Typography.Text>}
                collapsible={collapsible ? 'disabled' : undefined}
                extra={
                    <>
                        {status === 'new' ||
                            <Button style={{ margin: '-5px' }} type="link" danger shape="circle" onClick={() => deleteConfigureMetric(idx)} icon={<CiTrash />} />
                        }
                    </>
                }
            >
                <Form
                    name="metrics"
                    form={form}
                    layout="vertical"
                    validateTrigger="onBlur"
                    initialValues={{
                        metric: metricLabel,
                        extractionApproach: data.extractionApproach,
                        extractionText: data.extractionText,
                        conversion: data.conversion,
                        units: data.units,
                    }}
                    onValuesChange={(changedValues, allValues) => {
                        if (changedValues.metric !== undefined) {
                            updateFieldData({ metric: changedValues.metric }, idx);
                        }

                        if (changedValues.extractionApproach !== undefined) {
                            updateFieldData(
                                {
                                    extractionText: undefined,
                                    extractionApproach: changedValues.extractionApproach,
                                },
                                idx
                            );
                        }

                        if (changedValues.extractionText !== undefined) {
                            updateFieldData({ extractionText: changedValues.extractionText }, idx);
                        }

                        if (changedValues.conversion !== undefined) {
                            updateFieldData({ conversion: changedValues.conversion }, idx);
                        }

                    }}
                >
                    {/* Metric */}
                    <Form.Item
                        label={<Text strong>Metric</Text>}
                        name="metric"
                        rules={[{ required: true, message: 'Please select a metric!' }]}
                    >
                        <Select
                            showSearch
                            filterOption={(input, option) =>
                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                            options={metricsData}
                            placeholder={
                                <Text type="secondary" style={{ height: '100%', display: 'flex', alignItems: 'center' }}>
                                    Select a metric
                                </Text>
                            }
                        />
                    </Form.Item>

                    {/* Extraction */}
                    <Form.Item
                        label={<Text strong>Extraction Approach</Text>}
                        name="extractionApproach"
                        rules={[{ required: true, message: 'Please select an extraction approach!' }]}
                        style={{ marginBottom: '4px' }}
                    >
                        <Radio.Group>
                            <Radio value={MappingMode.SLUG}>Field Input</Radio>
                            <Radio value={MappingMode.USER_FORMULA}>Formula Input</Radio>
                            <Radio value={MappingMode.BUILTIN_FUNCTION}>Built-in Function</Radio>
                        </Radio.Group>
                    </Form.Item>

                    {data.extractionApproach === MappingMode.SLUG && (
                        <Form.Item
                            name="extractionText"
                            rules={[{ required: true, message: 'Please select a field' }]}
                        >
                            <Select
                                showSearch
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                options={metricMappingSampleDatapoints}
                                placeholder={
                                    <Text type="secondary" style={{ height: '100%', display: 'flex', alignItems: 'center' }}>
                                        Select a field
                                    </Text>
                                }
                                notFoundContent={
                                    <Text type="secondary">
                                        Please upload file / API key to get list of fields
                                    </Text>
                                }
                            />
                        </Form.Item>
                    )}

                    {data.extractionApproach === MappingMode.USER_FORMULA && (
                        <Form.Item
                            name="extractionText"
                            rules={[{ required: true, message: 'Please input a formula' }]}
                        >
                            <FormulaInput
                                data={data}
                                updateFieldData={updateFieldData}
                                idx={idx}
                                placeholder='("field1" + "field2") / "field3"'
                                sampleData={sampleData}
                            />
                        </Form.Item>
                    )}

                    {data.extractionApproach === MappingMode.BUILTIN_FUNCTION && (
                        <Form.Item
                            name="extractionText"
                            rules={[{ required: true, message: 'Please select a function' }]}
                        >
                            <Select
                                showSearch
                                filterOption={(input, option) =>
                                    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                }
                                options={metricsBuiltinFormula}
                                placeholder={
                                    <Text type="secondary" style={{ height: '100%', display: 'flex', alignItems: 'center' }}>
                                        Select a function
                                    </Text>
                                }
                            />
                        </Form.Item>
                    )}

                    {/* Conversion */}
                    <Form.Item
                        label={<Text strong>Conversion</Text>}
                        name="conversion"
                    >
                        <Select
                            showSearch
                            filterOption={(input, option) =>
                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                            options={metricsMappingConversion}
                            placeholder={
                                <Text type="secondary" style={{ height: '100%', display: 'flex', alignItems: 'center' }}>
                                    Select a conversion
                                </Text>
                            }
                        />
                    </Form.Item>

                    {/* Units */}
                    <Form.Item
                        label={<Text strong>Units</Text>}
                        name="units"
                    >
                        <Input
                            maxLength={100}
                            placeholder="Km, mph ..."
                            className="gps-metric-mapping-input"
                            onBlur={(e) => updateFieldData({ units: e.target.value }, idx)}
                        />
                    </Form.Item>
                </Form>
            </Panel>
        </Collapse>
    )
}

interface IMetric {
    status: "new" | "old",
    data: IConfigureMetric,
    metricsData: IMetricsMapping[],
    idx: number,
    updateFieldData: (
        updates: Partial<IConfigureMetric>,
        index: number
    ) => void,
    deleteConfigureMetric: (idx: number) => void,
    collapsible?: boolean,
    metricMappingSampleDatapoints: { label: string, value: string }[],
    metricsBuiltinFormula: { value: string, label: string }[],
    metricsMappingConversion: { value: string, label: string }[],
    open?: boolean,
    sampleData?: ISampleDataPointDataType[]
}

export default Metric;
